/** Metadata types mirroring backend/app/schemas/meta.py, plus the data-API shapes. */

import { useQuery } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import type { RecordScope } from '@/lib/types'
import { formatDate, formatDateTime } from '@/lib/utils'

export type SemanticType =
  | 'text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'url'
  | 'location'
  | 'sequence'
  | 'select'
  | 'multi_select'
  | 'integer'
  | 'decimal'
  | 'percent'
  | 'currency'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'json'
  | 'attachment'
  | 'reference'
  | 'record'
  | 'user'
  | 'users'
  | 'computed'

export interface FieldOption {
  value: string
  label?: string
  tone?: string
  /** How the choice appears in a sequence number ("BUG"). */
  code?: string
}

export interface FieldDef {
  id: string
  slug: string
  label: string
  help_text: string
  type: SemanticType
  required: boolean
  options: FieldOption[] | null
  config: Record<string, unknown>
  position: number
  /** `system`: a column every table has (Owner), placed only where a view asks. */
  status: 'active' | 'retired' | 'system'
  references: string | null
  /** A new record's value when none is given, stored as text (design.md §P14). */
  default?: string | null
}

export type ViewKind =
  | 'list'
  | 'board'
  | 'calendar'
  | 'form'
  | 'detail'
  | 'document'
  | 'dashboard'
  | 'report'

export interface ViewFilter {
  field: string
  op: string
  value?: unknown
}

export interface ViewConfig {
  columns?: string[]
  sort?: { field: string; dir: 'asc' | 'desc' }[]
  filters?: ViewFilter[]
  group_by?: string
  date_field?: string
  card_fields?: string[]
  /** A list: grouped by up to two fields, a date by its bucket (`expected_close:month`). */
  groups?: string[]
  totals?: string[]
  /** A list: the choice fields its toolbar offers as dropdowns; the first two otherwise. */
  quick_filters?: string[]
  /** A list or board: the date field(s) its toolbar offers a period for ("Received on:
   *  this month"); a pipeline offers Expected close and Last activity on. */
  date_filter?: string | string[]
  /** A board: columns reached through an action, not a plain move ({"deploy": "mark_deployed"}). */
  moves?: Record<string, string>
  sections?: { title: string; fields: string[] }[]
  /** A form: child records typed in the same form and saved with the record. */
  lines?: FormLines[]
  mine_only?: boolean
  /** detail and dashboard pages — backend app/meta/layout.py, design.md §P13 */
  layout?: Block[]
  /** A dashboard's pickers above the page: a period and an owner (design.md §P13). */
  controls?: PageControlsConfig
  /** A report: the fields its reader may narrow it by (design.md §P19). */
  filter_by?: ReportFilter[]
  /** A report or a document: the paper it prints on. */
  paper?: Paper
  /** A document: a rule saying when the record has it at all (design.md §P19). */
  show_when?: unknown
  /** An app page: the lowest level of the app that sees it. */
  level?: 'member' | 'manager' | 'admin'
}

/** A master's details: rows of `table`, whose `field` links them to the record. */
export interface FormLines {
  table: string
  field: string
  title?: string
  columns?: string[]
  /** Offered in the edit form too: the lines the record has, to change, add to or
   *  archive (design.md §P7). */
  edit?: boolean
}

export interface ReportFilter {
  table: string
  field: string
  label?: string
}

export interface Paper {
  size?: 'A4' | 'A3' | 'Letter' | 'Legal'
  orientation?: 'portrait' | 'landscape'
}

export interface PageControlsConfig {
  /** `options`: the periods this page offers, in this order, when not every one suits. */
  period?: boolean | { default?: PeriodPreset; options?: PeriodPreset[] }
  owner?: boolean
}

export type PeriodPreset =
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'last_18_months'
  | 'this_fy'
  | 'last_fy'
  | 'fy_before_last'

export type Bucket = 'day' | 'week' | 'month' | 'quarter' | 'year'

/** What a widget on a dashboard may say about the page's pickers: the date its
 *  numbers are placed in time by (`period`), and whether it follows the owner
 *  picker (`owner`, on by default). */
export interface Timed {
  period?: string
  owner?: boolean
}

export interface ViewDef {
  id: string
  slug: string
  name: string
  kind: ViewKind
  config: ViewConfig
  is_default: boolean
  /** A personal view: the caller's own, saved without a plan. */
  personal?: boolean
}

// --- page layouts (design.md §P13) -------------------------------------------------

/** Ties a widget on a detail page to the record shown: rows whose `field` equals
 *  this record's id, or this record's `value_from` field. */
export interface Match {
  field: string
  value_from?: string
}

/** Any widget on a detail page may be shown only while a rule on the record holds
 *  (design.md §P13), e.g. a Branches list only on a Group. */
export type Block = (
  | { type: 'columns'; columns: { span?: number; blocks: Block[] }[] }
  | { type: 'tabs'; tabs: { title: string; blocks: Block[] }[] }
  | { type: 'fields'; title?: string; fields?: string[]; rest?: boolean }
  | {
      type: 'related'
      title?: string
      table?: string
      field?: string
      rest?: boolean
      quick_add?: boolean
      limit?: number
      /** Shown after each row's name, on the same line ("· Principal · 98290 12345"). */
      fields?: string[]
    }
  | ({
      type: 'records'
      title?: string
      table: string
      display?: 'list' | 'board' | 'calendar'
      columns?: string[]
      filters?: ViewFilter[]
      sort?: { field: string; dir: 'asc' | 'desc' }[]
      limit?: number
      group_by?: string
      date_field?: string
      match?: Match
      /** A + that adds a record here, with the link to the page's record filled in. */
      quick_add?: boolean
      /** A list: number columns summed in a foot row, over everything that matches. */
      totals?: string[]
      /** A list: each record with its child rows under it (master and details). */
      details?: { table: string; field: string; columns: string[]; totals?: string[] }
      /** A list read like the table's own: search, dropdowns, a period, pages; `start`
       *  narrows it as it opens, and the reader may clear it. */
      toolbar?: { quick_filters?: string[]; date_filter?: string | string[]; start?: ViewFilter[] }
    } & Timed)
  | ({
      type: 'metric'
      title: string
      table: string
      agg?: Aggregate
      field?: string
      filters?: ViewFilter[]
      match?: Match
      /** With a period: the change against the period before, of the same length. */
      compare?: 'previous_period'
      /** With no period, a standing total: how to tell what it stood at when the
       *  period began. A record joins it on `from` (its creation otherwise) and leaves
       *  on `until`; `filters` is what is true of the ones that have left since. */
      standing?: { until: string; from?: string; filters?: ViewFilter[] }
      /** Which way is good news: more won deals, fewer lost ones. */
      better?: 'higher' | 'lower'
    } & Timed)
  | ({
      type: 'chart'
      title: string
      table: string
      chart?: 'bar' | 'funnel'
      group_by: string
      /** When group_by is a date: the span each bar covers. */
      bucket?: Bucket
      agg?: Aggregate
      field?: string
      filters?: ViewFilter[]
      match?: Match
      /** Bars shown before "Show all": the top ones, or a trend's latest. */
      limit?: number
    } & Timed)
  | ({
      type: 'breakdown'
      title: string
      table: string
      group_by: string
      columns: BreakdownColumn[]
      /** A column title to sort rows by; the first column otherwise. */
      sort?: string
      /** Fields of the linked record each row stands for, shown beside its name. */
      details?: string[]
      /** A field of that record to gather the rows under headings by (a department). */
      sections?: string
      /** A subtotal row under each heading. */
      subtotals?: boolean
      limit?: number
    } & Timed)
  | {
      type: 'roster'
      title: string
      /** `day`: one day as a list, its rows under what their day is (design.md §P13). */
      display?: 'grid' | 'day'
      /** The rows: one per record of this table. */
      table: string
      filters?: ViewFilter[]
      /** A field of the rows to gather them under headings by. */
      group_by?: string
      /** Fields of the rows shown beside the name. */
      columns?: string[]
      /** How many days; 30 when not given. They end today, or `ahead` days after it. */
      days?: number
      /** Days after today inside the window, so planned leave shows. */
      ahead?: number
      /** `month`: one calendar month, this one first, with previous / next. */
      window?: 'days' | 'month'
      /** Weekdays nobody is expected on: left blank instead of `missing`. */
      off_days?: Weekday[]
      /** What a day can show, in the order that wins when two apply. */
      marks: RosterMark[]
      /** A day up to today with no mark at all; `when` (a rule over the row) says which
       *  rows it counts against — not a consultant's. */
      missing?: Mark & { since?: string; until?: string; when?: unknown }
      /** Days nobody works, from a table (holidays): this mark on every row, never
       *  `missing`. A row with a record of the mark coded `present`'s table that day
       *  shows that mark instead (someone who checks in on a holiday is Present). */
      days_off?: Mark & {
        table: string
        date: string
        name?: string
        filters?: ViewFilter[]
        present?: string
      }
      limit?: number
      owner?: boolean
    }
  | {
      type: 'matrix'
      title: string
      table: string
      /** The rows: what the records are grouped by — a linked record, a person, a choice. */
      group_by: string
      /** The date that places a record in a column. */
      date: string
      /** What a column spans; a month when not given. */
      bucket?: Bucket
      agg?: 'count' | 'sum'
      field?: string
      filters?: ViewFilter[]
      /** Titles a column showing each row's earliest date; rows are ordered by it. */
      first?: string
      /** Fields of the linked record each row stands for, shown beside its name. */
      columns?: string[]
      limit?: number
      owner?: boolean
    }
  | {
      /** One day's records on a map (design.md §P13): initials at each place. */
      type: 'map'
      title?: string
      table: string
      /** A place field ("lat,lng"). */
      location: string
      /** The date that puts a record on a day. */
      date: string
      /** A link or a person whose initials and name a marker shows; the record's own
       *  name otherwise. */
      who?: string
      /** Fields said on hover, after the name. */
      details?: string[]
      filters?: ViewFilter[]
      /** Where it opens on a day with nothing: [latitude, longitude], and how close. */
      center?: [number, number]
      zoom?: number
    }
  | { type: 'timeline'; title?: string }
  | { type: 'actions'; only?: string[]; table?: string }
  | { type: 'note'; title?: string; text: string }
  | { type: 'heading'; text: string }
  /** Ruled lines to sign on paper, each over who signs there. */
  | { type: 'signatures'; labels: string[] }
  /** What follows starts a new printed page; nothing on screen. */
  | { type: 'page_break' }
  /** A document's top: the organisation's own details. */
  | { type: 'letterhead' }
  | { type: 'summary'; rows: SummaryRow[] }
) & { show_when?: unknown }

/** A label and an amount: a number field of the record, or a rule over its fields. */
export interface SummaryRow {
  label: string
  field?: string
  value?: unknown
  strong?: boolean
}

export type Aggregate = 'count' | 'sum' | 'avg' | 'min' | 'max'

/** One number per row of a breakdown; may read another table with the same group_by. */
export interface BreakdownColumn {
  title: string
  /** false: not totalled (days present do not add up across people). */
  total?: boolean
  table?: string
  agg?: Aggregate
  field?: string
  filters?: ViewFilter[]
  period?: string
}

export type Tone = 'neutral' | 'accent' | 'success' | 'warn' | 'danger' | 'info'
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

/** What a roster cell shows: a label, a tone for its colour, a short code. */
export interface Mark {
  label: string
  tone: Tone
  code?: string
}

/** A mark and the records that earn it: `row` links them to the roster's rows, and the
 *  day is their `date`, or every day from `from` to `to`. */
export interface RosterMark extends Mark {
  table: string
  row: string
  date?: string
  from?: string
  to?: string
  filters?: ViewFilter[]
  /** On a day list: this field of the record, beside the name (a check-in's time); the
   *  heading is ordered by it, latest first. */
  show?: string
}

/** Mirrors DEFAULT_DETAIL_LAYOUT in backend app/meta/layout.py — keep them equal.
 *  `rest` widgets show whatever is not placed elsewhere, so this layout looks the
 *  same as the page did before layouts existed, and keeps up with new fields. */
export const DEFAULT_DETAIL_LAYOUT: Block[] = [
  {
    type: 'columns',
    columns: [
      { span: 8, blocks: [{ type: 'fields', rest: true }] },
      { span: 4, blocks: [{ type: 'related', rest: true }, { type: 'timeline' }] },
    ],
  },
]

/** A button on this table's records (design.md §P15). */
export interface ActionDef {
  slug: string
  label: string
  confirm: string | null
  tone: string
  /** Shown only while this rule holds for the record; the server checks again. */
  condition: unknown
  /** Fields of this record the button asks for before it runs. */
  inputs?: { field: string; required: boolean; blank?: boolean }[]
  /** `table`: a button on the table itself (its list page, a dashboard block). */
  on?: 'record' | 'table'
  /** For a table action: the record it acts on, or `expect: none` to create one. */
  find?: { where: ViewFilter[]; expect?: 'one' | 'none'; done?: string; missing?: string }
}

export interface ChildList {
  table: string
  field: string
  label: string
  /** What the link field says its list shows beside each name (`inverse_fields`). */
  fields?: string[]
}

export interface TableDef {
  id: string
  key: string
  app: string
  slug: string
  label: string
  label_plural: string
  description: string
  physical_name: string
  status: string
  label_field: string | null
  fields: FieldDef[]
  views: ViewDef[]
  children: ChildList[]
  actions: ActionDef[]
  /** What integrations used with this table offer (integrations/offers.py). */
  offers?: OfferDef[]
  can: Record<'read' | 'add' | 'change' | 'archive' | 'export', boolean>
  /** The view this person opens the table on, when they chose one (design.md §P25). */
  my_default?: string | null
}

/** An input an integration's offer asks for. */
export interface OfferInput {
  slug: string
  label?: string
  type: 'text' | 'long_text' | 'reference' | 'select' | 'variables' | 'attachment'
  required?: boolean
  references?: string
  filter?: ViewFilter[]
  /** variables: the reference input whose record holds the text with {{1}}, {{2}}… */
  from?: string
  field?: string
  /** variables: the chosen record's field that fills {{n}} ("value_{n}"), when the
   *  record it reads has what it names; the sender fills the rest. */
  defaults?: string
  help_text?: string
}

/** An integration's action on this table ("Send WhatsApp"), for one record or many. */
export interface OfferDef {
  integration: string
  integration_name: string
  slug: string
  label: string
  tone: string
  confirm?: string | null
  inputs: OfferInput[]
  /** Opened right after this button, with these choices filled in (design.md §P20). */
  after?: { action: string; inputs: Record<string, unknown> } | null
}

/** A file an attachment field holds (routers/files.py). */
export interface FileRef {
  id: string
  name: string
  type: string
  size: number
}

/** A record is an open map: the fields are whatever metadata says they are. */
export type DataRecord = Record<string, unknown> & { id: string }

export interface RecordPage {
  items: DataRecord[]
  total: number
  page: number
  page_size: number
  scope: RecordScope
  /** table key (or "sys_user") -> record id -> display label */
  refs: Record<string, Record<string, string>>
}

export interface SchemaChange {
  id: string
  intent: string
  status: 'planned' | 'applied' | 'rejected' | 'failed' | 'expired'
  risk: 'low' | 'medium' | 'high'
  reversible: boolean
  affected_rows: number
  touches_existing: boolean
  diff: Record<string, unknown>[]
  ddl: string[]
  post_ddl: string[]
  backfill_sql: string[]
  unconvertible: { table: string; field: string; count: number; sample: { id: string; value: string }[] }[]
  layout_only: boolean
  ops: Record<string, unknown>[]
  added_at: string
  expires_at: string
  source?: string
  error?: string | null
  applied_at?: string | null
  /** Who asked for it when not the panel: "Claude", "ChatGPT" (connectors.md). */
  via?: string | null
}

// --- system fields --------------------------------------------------------------

/** Every table has an owner (a system column). It is offered like a user field, but
 *  appears only where a list, form or page places `owner` — never on its own. */
export const OWNER_FIELD: FieldDef = {
  id: 'system:owner',
  slug: 'owner',
  label: 'Owner',
  help_text: 'Who this record belongs to. Records start with whoever creates them.',
  type: 'user',
  required: false,
  options: null,
  config: {},
  position: 10_000,
  status: 'system',
  references: 'sys_user',
}

const tracking = (slug: string, label: string, type: SemanticType, help: string): FieldDef => ({
  id: `system:${slug}`,
  slug,
  label,
  help_text: help,
  type,
  required: false,
  options: null,
  config: {},
  position: 10_000,
  status: 'system',
  references: type === 'user' ? 'sys_user' : null,
})

/** The columns every table has and the platform fills (backend meta/types.py
 *  SYSTEM_FIELDS): placeable in lists, pages and filters, never typed into. */
export const SYSTEM_FIELDS: FieldDef[] = [
  OWNER_FIELD,
  tracking('added_at', 'Added on', 'datetime', 'When the record was added.'),
  tracking('added_by', 'Added by', 'user', 'Who added the record.'),
  tracking('changed_at', 'Last changed on', 'datetime', 'When the record last changed.'),
  tracking('changed_by', 'Last changed by', 'user', 'Who last changed the record.'),
]
/** Added on and by, Last changed on and by: shown on every record page. */
export const TRACKING_FIELDS = SYSTEM_FIELDS.filter((f) => f.slug !== OWNER_FIELD.slug)

export function withSystemFields(table: TableDef): TableDef {
  const missing = SYSTEM_FIELDS.filter((s) => !table.fields.some((f) => f.slug === s.slug))
  return missing.length ? { ...table, fields: [...table.fields, ...missing] } : table
}

/** A field a view may name: the table's own active fields and the system ones. */
export function placeable(field: FieldDef): boolean {
  return field.status === 'active' || field.status === 'system'
}

// --- hooks ------------------------------------------------------------------------

export function useAppTables(appSlug: string) {
  return useQuery({
    queryKey: ['meta', 'app', appSlug],
    queryFn: async () =>
      (await api.get<TableDef[]>(`/api/meta/apps/${appSlug}/tables`)).map(withSystemFields),
    staleTime: 60_000,
    enabled: Boolean(appSlug),
  })
}

/** An app's own pages (dashboards and reports). */
export function useAppPages(appSlug: string) {
  return useQuery({
    queryKey: ['meta', 'pages', appSlug],
    queryFn: () => api.get<ViewDef[]>(`/api/meta/apps/${appSlug}/pages`),
    staleTime: 60_000,
    enabled: Boolean(appSlug),
  })
}

export interface AggregateResult {
  value?: number | string | null
  /** `label` names a person or a linked record; a date bucket's key is its first day. */
  groups?: { key: unknown; value: number | string | null; label?: string }[]
  scope: RecordScope
}

export interface AggregateOptions {
  agg?: Aggregate
  field?: string
  groupBy?: string
  bucket?: Bucket
  filters?: ViewFilter[]
  search?: string
}

/** The query behind `useAggregate`, for widgets that run several side by side. */
export function aggregateQuery(tableKey: string, options: AggregateOptions, enabled = true) {
  const [app, slug] = tableKey.split('.')
  const { agg = 'count', field, groupBy, bucket, filters = [], search } = options
  return {
    queryKey: ['data', tableKey, 'aggregate', agg, field, groupBy, bucket, filters, search],
    queryFn: () =>
      countTogether({
        table: tableKey,
        agg,
        field,
        group_by: groupBy,
        bucket,
        filters,
        search: search || undefined,
      }),
    enabled: enabled && Boolean(app && slug),
  }
}

/** Numbers asked for in the same moment — a dashboard's tiles, charts and totals — go
 *  to the server as one request (`POST /api/data/-/aggregates`), each answered or
 *  refused on its own, so a tile someone may not see fails only that tile. */
const TOGETHER = 60
let waiting: {
  query: Record<string, unknown>
  resolve: (result: AggregateResult) => void
  reject: (error: unknown) => void
}[] = []
let flushing: ReturnType<typeof setTimeout> | null = null

function countTogether(query: Record<string, unknown>): Promise<AggregateResult> {
  return new Promise((resolve, reject) => {
    waiting.push({ query, resolve, reject })
    flushing ??= setTimeout(sendTogether, 0)
  })
}

function sendTogether() {
  const taken = waiting
  waiting = []
  flushing = null
  for (let start = 0; start < taken.length; start += TOGETHER) {
    const batch = taken.slice(start, start + TOGETHER)
    api
      .post<{ results: Answer[] }>('/api/data/-/aggregates', {
        queries: batch.map((one) => one.query),
      })
      .then(({ results }) =>
        batch.forEach((one, index) => {
          const answer = results[index]
          if (answer && 'data' in answer) one.resolve(answer.data)
          else
            one.reject(
              new ApiError(
                answer?.error.status ?? 500,
                answer?.error.code ?? 'internal_error',
                answer?.error.message ?? 'Could not count this.',
                answer?.error.details,
              ),
            )
        }),
      )
      .catch((error: unknown) => batch.forEach((one) => one.reject(error)))
  }
}

type Answer =
  | { data: AggregateResult }
  | { error: { status: number; code: string; message: string; details?: unknown } }

/** One number, or one per group — metric tiles, charts and list totals. */
export function useAggregate(tableKey: string, options: AggregateOptions, enabled = true) {
  return useQuery(aggregateQuery(tableKey, options, enabled))
}

/** One table's definition; also for `useQueries` over several. */
export function tableQuery(tableKey: string) {
  return {
    queryKey: ['meta', 'table', tableKey],
    queryFn: async () => withSystemFields(await api.get<TableDef>(`/api/meta/tables/${tableKey}`)),
    staleTime: 60_000,
    enabled: Boolean(tableKey),
  }
}

export function useTable(tableKey: string) {
  return useQuery(tableQuery(tableKey))
}

/** Fields in the order a view asks for, falling back to metadata order. */
export function columnsFor(table: TableDef, view: ViewDef | undefined): FieldDef[] {
  const active = table.fields.filter((f) => f.status === 'active')
  const wanted = view?.config.columns
  if (!wanted?.length) return active.slice(0, 6)
  return wanted
    .map((slug) => table.fields.find((f) => f.slug === slug && placeable(f)))
    .filter((f): f is FieldDef => Boolean(f))
}

/** A computed field seen as the stored field it behaves like: a lookup of a select
 *  is a select, a formula with result_type currency is currency (data/computed.py). */
export function valueField(field: FieldDef): FieldDef {
  if (field.type !== 'computed') return field
  const result = field.config.result_type as SemanticType | undefined
  return { ...field, type: result && result !== 'computed' ? result : 'text' }
}

export function labelOf(table: TableDef, record: DataRecord): string {
  // The server works the label out (data/query.py label_sql), following the label
  // field when it is a link: an Opportunity is known by its Institution.
  if (typeof record._label === 'string' && record._label) return record._label
  const slug = table.label_field
  const field = table.fields.find((f) => f.slug === slug)
  const value = slug && field?.type !== 'reference' ? record[slug] : null
  if (!value) return `${table.label} ${String(record.id).slice(0, 8)}`
  if (field?.type !== 'long_text') return String(value)
  // A message known by its text: its first line, cut short (data/query.py short_label).
  const text = String(value)
  const first = text.split('\n')[0]
  return first.length > 80 || text.includes('\n') ? `${first.slice(0, 79)}…` : first
}

/** A cell as plain text — for a link's text, where the cell's own link can't nest. */
export function cellText(field: FieldDef, record: DataRecord, refs: RecordPage['refs']): string {
  const value = record[field.slug]
  if (value === null || value === undefined || value === '') return ''
  if (field.type === 'reference') return refLabel(refs, field, value) ?? ''
  if (field.type === 'user') return refs['sys_user']?.[String(value)] ?? ''
  if (field.type === 'users') {
    return Array.isArray(value)
      ? value.map((id) => refs['sys_user']?.[String(id)] ?? '').filter(Boolean).join(', ')
      : ''
  }
  if (field.type === 'record') {
    const [key, id] = String(value).split(':')
    return refs[key]?.[id] ?? ''
  }
  if (field.type === 'select') {
    return field.options?.find((o) => o.value === value)?.label ?? String(value)
  }
  if (field.type === 'date') return formatDate(String(value)) ?? String(value)
  if (field.type === 'datetime') return formatDateTime(String(value)) ?? String(value)
  return String(value)
}

/** Resolves a reference id to its display label using the page's `refs` map. */
export function refLabel(
  refs: RecordPage['refs'],
  field: FieldDef,
  value: unknown,
): string | null {
  if (!value || !field.references) return null
  return refs[field.references]?.[String(value)] ?? null
}
